import { Button, Input, Modal, Radio, Tooltip } from 'antd'
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`

const ModalFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ModalFieldLabel = styled.label`
  font-weight: 500;
`

const RequiredAsterisk = styled.span`
  color: #ff4d4f;
`

const LabelRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`

const HelperText = styled.div`
  font-size: 0.85rem;
  color: #8c8c8c;
`

interface ComputedOutcomeModalProps {
  open: boolean
  onClose: () => void
  computedOutcome: boolean
  currentOverride: boolean | undefined
  currentJustification: string | undefined
  onSave: (
    outcome: boolean | undefined,
    justification: string | undefined
  ) => void
  title?: string
  passLabel?: string
  failLabel?: string
}

const ModalField: FC<{
  label: string
  required?: boolean
  children: ReactNode
}> = ({ label, required = false, children }) => {
  return (
    <ModalFieldContainer>
      <ModalFieldLabel>
        <LabelRow>
          {label}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </LabelRow>
      </ModalFieldLabel>
      {children}
    </ModalFieldContainer>
  )
}

/**
 * A modal for overriding computed section outcomes.
 * Allows the user to select pass/fail and provide a justification.
 */
export const ComputedOutcomeModal: FC<ComputedOutcomeModalProps> = ({
  open,
  onClose,
  computedOutcome,
  currentOverride,
  currentJustification,
  onSave,
  title = 'Edit Outcome',
  passLabel = 'Pass',
  failLabel = 'Fail',
}) => {
  const [modalOutcome, setModalOutcome] = useState<boolean>(true)
  const [modalJustification, setModalJustification] = useState('')

  const isOverridden = currentOverride !== undefined
  const finalOutcome = currentOverride ?? computedOutcome

  useEffect(() => {
    if (open) {
      setModalOutcome(finalOutcome)
      setModalJustification(currentJustification ?? '')
    }
  }, [open, finalOutcome, currentJustification])

  const hasChanges = modalOutcome !== computedOutcome
  const canSave = useMemo(() => {
    if (!hasChanges) {
      return true
    }
    return modalJustification.trim() !== ''
  }, [hasChanges, modalJustification])

  const handleSave = useCallback(() => {
    if (modalOutcome === computedOutcome) {
      onSave(undefined, undefined)
      onClose()
      return
    }
    onSave(modalOutcome, modalJustification.trim())
    onClose()
  }, [modalOutcome, modalJustification, computedOutcome, onSave, onClose])

  const handleReset = useCallback(() => {
    onSave(undefined, undefined)
    onClose()
  }, [onSave, onClose])

  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  const handleJustificationChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setModalJustification(e.target.value)
    },
    []
  )

  const saveButtonDisabled = !canSave || !hasChanges

  return (
    <Modal
      title={title}
      open={open}
      onCancel={handleCancel}
      footer={
        <ModalFooter>
          <Button onClick={handleReset} disabled={!isOverridden}>
            Reset to Computed
          </Button>
          <Button onClick={handleCancel}>Cancel</Button>
          <Tooltip
            open={!canSave ? undefined : false}
            title={
              !canSave
                ? 'A justification is required when overriding the computed outcome.'
                : ''
            }>
            <Button
              type={!saveButtonDisabled ? 'primary' : 'default'}
              onClick={handleSave}
              disabled={saveButtonDisabled}>
              Save
            </Button>
          </Tooltip>
        </ModalFooter>
      }>
      <ModalContent>
        <ModalField label='Outcome'>
          <Radio.Group
            value={modalOutcome}
            onChange={(e) => {
              setModalOutcome(e.target.value)
            }}>
            <Radio value={true}>{passLabel}</Radio>
            <Radio value={false}>{failLabel}</Radio>
          </Radio.Group>
          {modalOutcome === computedOutcome && (
            <HelperText>This matches the computed outcome.</HelperText>
          )}
        </ModalField>
        <ModalField
          label='Justification'
          required={modalOutcome !== computedOutcome}>
          <Input.TextArea
            rows={3}
            placeholder='Provide a reason for overriding the computed outcome...'
            value={modalJustification}
            onChange={handleJustificationChange}
          />
        </ModalField>
      </ModalContent>
    </Modal>
  )
}
